import React from 'react';
import { AxiosError, AxiosResponse } from 'axios';
import { Chip, FormHelperText, Grid, TextField, Typography } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';

import { RevisionContext } from '../RevisionContext';
import { HomeContext } from '../../../Home/HomeContext';
import { ExtraParameterModel } from '../../../../Models/ExtraParameterModel';
import { extraParametersBatchModelInitial } from '../../../../Models/ExtraParametersBatchModel';
import ExtraParameterService from '../../../../Services/ExtraParameterService';
import { BasicConfirmDialog } from '../../../../shared/components/BasicConfirmDialog';
import { ExtraParameterFieldType } from '../../../../shared/enum/ExtraParameterFieldType';
import { theme } from '../../../../theme';
import CategoryExtraParametersService from '../../../../Services/CategoryExtraParametersService';
import { BatchCorrectionContext } from '../BatchCorrectionContext';

export function ExtraParametersBatchCorrection() {

    const revisionContext = React.useContext(RevisionContext);
    const homeContext = React.useContext(HomeContext);
    const batchCorrectionContext = React.useContext(BatchCorrectionContext);

    const [extraParameters, setExtraParameters] = React.useState<ExtraParameterModel[]>([]);

    const [extraParameterError, setExtraParameterError] = React.useState(false);

    React.useEffect(() => {
        if (revisionContext.filterClassification.clientId != null && revisionContext.extraParametersBatchOpenDialog) {
            ExtraParameterService
                .getExtraParameterByQuery(revisionContext.filterClassification.clientId, false)
                .then((res: AxiosResponse) => { setExtraParameters(res.data); })
                .catch((error: AxiosError) => { homeContext.showError('', error); });
        }
    }, [revisionContext.extraParametersBatchOpenDialog]);

    const hasError = () => {
        const errorExtraParameters = extraParameters.filter((e: ExtraParameterModel) => (e.value && e.value != '')).length == 0;

        setExtraParameterError(errorExtraParameters);

        return (errorExtraParameters);
    };

    const updateExtraParametersBatch = () => {
        if (!hasError() && extraParameters && extraParameters.length > 0) {
            homeContext.setOpenLoadingDialog(true);

            const extraParameterData = { ...extraParametersBatchModelInitial };
            extraParameterData.categoriesId = revisionContext.ids;
            const extraParametersList = JSON.parse(JSON.stringify(extraParameters));

            extraParameterData.extraParameters = extraParametersList.filter((e: ExtraParameterModel) => (e.value && e.value != ''));

            CategoryExtraParametersService.updateExtraParametersBatch(extraParameterData)
                .then(() => {
                    clearModalExtraParameters();
                    batchCorrectionContext.setCallSearch(true);
                    homeContext.setOpenLoadingDialog(false);

                    homeContext.showSuccess(`As alterações foram realizadas para todas as ${revisionContext.ids.length} categorizações selecionadas com sucesso!`);
                })
                .catch((error: AxiosError) => {
                    homeContext.showError(' Ops... Ocorreu um erro e não foi possível salvar a alteração de mensagem chave. '
                        + 'Por favor, tente novamente dentro de alguns instantes. <br/>', error);
                    homeContext.setOpenLoadingDialog(false);
                });
        }
    };

    const clearModalExtraParameters = () => {
        revisionContext.setExtraParametersBatchOpenDialog(false);
        setExtraParameters([]);
        setExtraParameterError(false);
    };

    const handleExtraParameterValueChange = (parameter: ExtraParameterModel, values: string | string[] | null) => {
        const listParameters = JSON.parse(JSON.stringify(extraParameters));
        const index = extraParameters.findIndex(p => p.id == parameter.id);
        const currentParameter = JSON.parse(JSON.stringify(extraParameters[index]));

        if (values != null) {
            setExtraParameterError(false);

            if (Array.isArray(values))
                currentParameter.value = values.join(';');
            else
                currentParameter.value = values;
        } else {
            setExtraParameterError(true);
            currentParameter.value = '';
        }

        listParameters[index] = currentParameter;

        setExtraParameters(listParameters);
    };

    const getExtraParameters = () => {
        return (
            <Grid container spacing={3}>
                {extraParameters && extraParameters.length > 0 && (
                    <>
                        <Grid item xs={12} >
                            <Typography variant="subtitle1">Análise de mensagem chave</Typography>
                        </Grid>

                        {extraParameters.sort((a, b) => a.id - b.id).map((parameter) => {
                            if (parameter.type == ExtraParameterFieldType.TEXT) {
                                return (
                                    <Grid item
                                        xs={12}
                                        sm={parameter.description ? (parameter.description.length > 40 ? 12 : 6) : 6}
                                        md={parameter.description ? (parameter.description.length > 60 ? 12 : 6) : 6}>
                                        <TextField
                                            id={parameter.description}
                                            key={parameter.id}
                                            label={parameter.description}
                                            value={parameter.value ? parameter.value : ''}
                                            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                                                handleExtraParameterValueChange(parameter, event.target.value)}
                                            variant="outlined"
                                            fullWidth />
                                    </Grid>
                                );
                            } else {
                                const multiple = parameter.type == ExtraParameterFieldType.SELECT_MULTIPLE;

                                return (
                                    <Grid item
                                        xs={12}
                                        sm={parameter.description ? (parameter.description.length > 40 ? 12 : 6) : 6}
                                        md={parameter.description ? (parameter.description.length > 60 ? 12 : 6) : 6}>
                                        <Autocomplete
                                            multiple={multiple}
                                            id={parameter.description}
                                            key={parameter.id}
                                            value={parameter.value
                                                ? (multiple ? parameter.value.split(';') : parameter.value)
                                                : (multiple ? [] : null)}
                                            onChange={(_, values: string | string[] | null) => handleExtraParameterValueChange(parameter, values)}
                                            options={parameter.answers ? parameter.answers.split(';') : []}
                                            getOptionLabel={(option: string) => option}
                                            filterSelectedOptions={multiple}
                                            noOptionsText="Sem resultados"
                                            loadingText="Carregando..."
                                            renderTags={(value, getTagProps) =>
                                                value.map((option, index) => (
                                                    <Chip
                                                        key={index}
                                                        variant="outlined"
                                                        label={option}
                                                        {...getTagProps({ index })} />
                                                ))
                                            }
                                            renderInput={(params) =>
                                                <TextField {...params}
                                                    label={parameter.description}
                                                    variant="outlined" />
                                            } />
                                    </Grid>
                                );
                            }
                        })}
                        <Grid item xs={12}>
                            <FormHelperText error>
                                {extraParameterError && 'É necessário preencher ao menos um dos campos.'}
                            </FormHelperText>
                        </Grid>
                    </>
                )}
            </Grid>
        );
    };

    return (
        <BasicConfirmDialog
            title={'Alteração de mensagens chave'}
            description={'Selecione as novas opções de resposta para as mensagens chave.'}
            handleFirstButton={() => clearModalExtraParameters()}
            handleSecondButton={() => updateExtraParametersBatch()}
            textFirstButton='DESCARTAR'
            textSecondButton='SALVAR'
            open={revisionContext.extraParametersBatchOpenDialog}
            children={getExtraParameters()}
            cssProperties={{
                minWidth: '800px',
                maxWidth: '900px',
                padding: '1rem',
                colorFirstButton: theme.palette.primary.main,
                justifyButtons: 'flex-end',
                titleVariant: 'h4',
                descriptionVariant: 'body1'
            }} />
    );
}